import { get, post, patch, Delete } from '@/utils/https';

/**
 * Récupérer toutes les entreprises
 * @param {Object} params - Paramètres de recherche et pagination
 * @returns {Promise}
 */
export const getAllCompanies = (params) => {
  return get('/api/app/v1/super-admin/companies', params);
};

/**
 * Créer une entreprise avec invitation admin
 * @param {Object} data - Données de l'entreprise et de l'admin
 * @returns {Promise}
 */
export const createCompanyWithAdmin = (data) => {
  return post('/api/app/v1/super-admin/companies', data);
};

/**
 * Mettre à jour les champs d'une entreprise (is_test, etc.)
 * @param {string} teamId - ID de la team
 * @param {Object} updates - Champs à mettre à jour
 * @returns {Promise}
 */
export const updateCompany = (teamId, updates) => {
  return patch('/api/app/v1/super-admin/companies', { team_id: teamId, ...updates });
};

/**
 * Récupérer tous les freelancers
 * @param {Object} params - Paramètres de recherche et pagination
 * @returns {Promise}
 */
export const getAllFreelancers = (params) => {
  return get('/api/app/v1/super-admin/freelancers', params);
};

/**
 * Basculer un freelance sur le parcours facture classique (opt-out e-facture).
 * @param {string} freelancerId
 * @param {boolean} electronicInvoicingExempt
 * @returns {Promise}
 */
export const updateFreelancerElectronicInvoicingExempt = (freelancerId, electronicInvoicingExempt) => {
  return patch('/api/app/v1/super-admin/freelancers', {
    freelancer_id: freelancerId,
    electronic_invoicing_exempt: electronicInvoicingExempt,
  });
};

/**
 * Récupérer les sociétés de portage (annuaire global) et invitations en cours.
 * @param {Object} params - page, limit, search, is_test (true|false)
 * @returns {Promise}
 */
export const getPortageCompanies = (params) => {
  return get('/api/app/v1/super-admin/portage-companies', params);
};

/**
 * Récupérer les administrateurs entreprise avec statut d'onboarding.
 * @param {Object} params - page, limit, search, is_test, status
 * @returns {Promise}
 */
export const getTeamAdmins = (params) => {
  return get('/api/app/v1/super-admin/team-admins', params);
};

/**
 * Mettre à jour les champs d'une société de portage (is_test, etc.)
 * @param {string} companyId - ID de la société de portage
 * @param {Object} updates - Champs à mettre à jour
 * @returns {Promise}
 */
export const updatePortageCompany = (companyId, updates) => {
  return patch('/api/app/v1/super-admin/portage-companies', {
    portage_company_id: companyId,
    ...updates,
  });
};

/**
 * Détail d'une société de portage (super-admin).
 * @param {string} companyId
 * @returns {Promise}
 */
export const getPortageCompanyDetail = (companyId) => {
  return get(`/api/app/v1/super-admin/portage-companies/${companyId}`);
};

/**
 * Upload un document KYC pour une société de portage (super-admin).
 * @param {string} companyId
 * @param {FormData} formData - category, file
 * @returns {Promise}
 */
export const uploadPortageCompanyDocument = (companyId, formData) => {
  return post(`/api/app/v1/super-admin/portage-companies/${companyId}/documents/upload`, formData);
};

/**
 * Enregistrer, supprimer ou vérifier un document KYC portage (super-admin).
 * @param {string} companyId
 * @param {Object} body - { action: 'save_document' | 'delete_document' | 'verify_document', ... }
 * @returns {Promise}
 */
export const mutatePortageCompanyDocuments = (companyId, body) => {
  return patch(`/api/app/v1/super-admin/portage-companies/${companyId}/documents`, body);
};

/**
 * Met à jour une affiliation portage (modèle umbrella/ESN, référent).
 * @param {string} companyId
 * @param {string} affiliationId
 * @param {{ portage_contact_id?: string|null, client_billing_visible?: boolean }} updates
 * @returns {Promise}
 */
export const updatePortageAffiliation = (companyId, affiliationId, updates) => {
  return patch(
    `/api/app/v1/super-admin/portage-companies/${companyId}/affiliations/${affiliationId}`,
    updates
  );
};

/**
 * Crée un référent portage (super-admin).
 * @param {string} companyId
 * @param {{ first_name: string, last_name: string, email: string, job_title?: string }} data
 * @returns {Promise}
 */
export const createPortageCompanyContact = (companyId, data) => {
  return post(`/api/app/v1/super-admin/portage-companies/${companyId}/contacts`, data);
};

/**
 * Met à jour un référent portage (super-admin).
 * @param {string} companyId
 * @param {string} contactId
 * @param {{ first_name?: string, last_name?: string, email?: string, job_title?: string }} data
 * @returns {Promise}
 */
export const updatePortageCompanyContact = (companyId, contactId, data) => {
  return patch(
    `/api/app/v1/super-admin/portage-companies/${companyId}/contacts/${contactId}`,
    data
  );
};

/**
 * Supprime un référent portage (super-admin).
 * 409 + items si des affiliations l'utilisent encore.
 * @param {string} companyId
 * @param {string} contactId
 * @param {{ hideMessageModal?: boolean }} [config]
 * @returns {Promise}
 */
export const deletePortageCompanyContact = (companyId, contactId, config = {}) => {
  return Delete(
    `/api/app/v1/super-admin/portage-companies/${companyId}/contacts/${contactId}`,
    {},
    { hideMessageModal: true, ...config }
  );
};

/**
 * Réattribue les affiliations puis supprime le référent portage.
 * @param {string} companyId
 * @param {string} contactId
 * @param {{ assignments: Array<{ id: string, kind: 'affiliation', new_contact_id: string }> }} data
 * @returns {Promise}
 */
export const reassignAndDeletePortageCompanyContact = (companyId, contactId, data) => {
  return post(
    `/api/app/v1/super-admin/portage-companies/${companyId}/contacts/${contactId}/reassign`,
    data,
    { hideMessageModal: true }
  );
};

/**
 * S'impersonner en tant qu'utilisateur
 * @param {string} userId - ID de l'utilisateur cible
 * @returns {Promise}
 */
export const impersonateUser = (userId) => {
  return post('/api/app/v1/super-admin/impersonate', {
    user_id: userId,
  });
};

export const stopImpersonate = () => {
  return post('/api/app/v1/super-admin/stop-impersonate', {}, { hideMessageModal: true });
};

/**
 * Renvoyer une invitation pour une company
 * @param {string} teamId - ID de la company
 * @returns {Promise}
 */
export const resendCompanyInvitation = (teamId) => {
  return post('/api/app/v1/super-admin/companies/resend-invitation', {
    team_id: teamId,
  });
};

/**
 * @param {{ teamId?: string, profileId?: string, reason: string, reason_detail?: string }} params
 */
export const deleteNonConvertedAccount = ({ teamId, profileId, reason, reason_detail }) => {
  const query = teamId ? { team_id: teamId } : { profile_id: profileId };
  return Delete('/api/app/v1/super-admin/companies', query, {
    data: { reason, reason_detail },
  });
};

/** @deprecated use deleteNonConvertedAccount */
export const deleteCompany = (companyId, payload) =>
  deleteNonConvertedAccount({ teamId: companyId, ...payload });

/**
 * Supprimer un freelance avec toutes ses données
 * @param {string} freelancerId - ID du freelance
 * @returns {Promise}
 */
export const deleteFreelancer = (freelancerId) => {
  return Delete('/api/app/v1/super-admin/freelancers', { freelancer_id: freelancerId });
};

/**
 * Récupérer tous les utilisateurs (admin et members) d'une company
 * @param {string} teamId - ID de la company
 * @returns {Promise}
 */
export const getCompanyUsers = (teamId) => {
  return get(`/api/app/v1/super-admin/companies/${teamId}/users`);
};

export const getCompanyOktaLog = (teamId) => {
  return get(`/api/app/v1/super-admin/companies/${teamId}/okta-log`);
};


/**
 * Récupérer tous les documents KYC des freelancers (super-admin)
 * @param {Object} params - Paramètres de recherche, filtres et pagination
 * @returns {Promise}
 */
export const getKYCDocuments = (params) => {
  return get('/api/app/v1/super-admin/kyc-documents', params);
};

/**
 * Liste des mandats de facturation électronique (super-admin)
 * @param {Object} params - search, mandate_status, pa_status, page, limit, is_test
 * @returns {Promise}
 */
export const getBillingMandates = (params) => {
  return get('/api/app/v1/super-admin/billing-mandates', params);
};

/**
 * Liste des jobs d'émission de factures électroniques (super-admin)
 * @param {Object} params - search, job_status, lifecycle_status, page, limit
 * @returns {Promise}
 */
export const getInvoiceEmissionJobs = (params) => {
  return get('/api/app/v1/super-admin/invoice-emission-jobs', params);
};

/**
 * Récap HT / TVA / TTC d'une facture électronique (super-admin)
 * @param {string} jobId
 * @returns {Promise}
 */
export const getInvoiceEmissionJobAmountRecap = (jobId) => {
  return get(`/api/app/v1/super-admin/invoice-emission-jobs/${jobId}/amount-recap`);
};

/**
 * Approuver ou refuser un document KYC
 * @param {string} documentId - ID du document
 * @param {Object} data - { action: 'approve' | 'reject', rejection_reason?: string, identity_document_type?: string }
 * @returns {Promise}
 */
export const reviewKYCDocument = (documentId, data) => {
  return patch(`/api/app/v1/super-admin/kyc-documents/${documentId}/review`, data);
};

/**
 * Mettre à jour le type de document d'identité (passport / id_card / driver_license)
 * @param {string} documentId - ID du document
 * @param {Object} data - { identity_document_type: 'passport' | 'id_card' | null }
 * @returns {Promise}
 */
export const updateKYCDocumentType = (documentId, data) => {
  return patch(`/api/app/v1/super-admin/kyc-documents/${documentId}/type`, data);
};

/**
 * Lancer la vérification automatique sur un document KYC (super-admin)
 * @param {string} documentId - ID du document
 * @returns {Promise}
 */
export const verifyKYCDocument = (documentId) => {
  return post(`/api/app/v1/super-admin/kyc-documents/${documentId}/verify`);
};

/**
 * Récupérer les équipes staffées gratuitement (super-admin) pour un mois donné
 * @param {string} month - Mois au format YYYY-MM
 * @param {Object} params - Paramètres (search)
 * @returns {Promise}
 */
export const getSuperAdminStaffedFree = (month, params = {}) => {
  return get('/api/app/v1/super-admin/staffed-free', { month, ...params });
};

export const pushSuperAdminStaffedFreeUsage = (teamId, month) => {
  return post('/api/app/v1/super-admin/staffed-free/push-usage', { team_id: teamId, month });
};

export const updateStaffedFreeSubscriptionAmount = (teamId, amount) => {
  return patch('/api/app/v1/super-admin/staffed-free', { team_id: teamId, subscription_amount: amount });
};

/**
 * Catalogue emails transactionnels (super-admin)
 * @param {Object} params - search, audience, category
 */
export const getEmailTemplates = (params) => {
  return get('/api/app/v1/super-admin/emails', params);
};

/**
 * Preview HTML d'un template email
 * @param {string} id
 * @param {Object} params - locale + overrides props
 */
export const getEmailTemplatePreview = (id, params) => {
  return get(`/api/app/v1/super-admin/emails/${id}/preview`, params);
};

/**
 * Envoyer un email de test (props sample) à une adresse
 * @param {string} id
 * @param {{ to: string, locale?: string }} data
 */
export const sendEmailTemplateTest = (id, data) => {
  return post(`/api/app/v1/super-admin/emails/${id}/send-test`, data);
};

export default {
  getAllCompanies,
  createCompanyWithAdmin,
  updateCompany,
  getAllFreelancers,
  updateFreelancerElectronicInvoicingExempt,
  getPortageCompanies,
  getTeamAdmins,
  getPortageCompanyDetail,
  uploadPortageCompanyDocument,
  mutatePortageCompanyDocuments,
  updatePortageAffiliation,
  createPortageCompanyContact,
  updatePortageCompanyContact,
  deletePortageCompanyContact,
  reassignAndDeletePortageCompanyContact,
  updatePortageCompany,
  impersonateUser,
  resendCompanyInvitation,
  deleteCompany,
  deleteFreelancer,
  getCompanyUsers,
  getSuperAdminStaffedFree,
  pushSuperAdminStaffedFreeUsage,
  getKYCDocuments,
  getBillingMandates,
  getInvoiceEmissionJobs,
  reviewKYCDocument,
  updateKYCDocumentType,
  verifyKYCDocument,
  getEmailTemplates,
  getEmailTemplatePreview,
  sendEmailTemplateTest,
};

